import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Chip, List, ListItemButton, ListItemText, Paper, Stack, Typography } from '@mui/material';
import { getCounselorRanking } from '../../api/dashboard';
import { useBranchFilter } from '../../context/branchContext';
import { EmptyState } from '../../components/EmptyState';

/** "Leads assigned to inactive counselors" (section 19/28) — reuses the
 * existing counselor-ranking endpoint (already returns every counselor
 * with at least one assigned lead, active or not) rather than a new
 * query. Clicking a row opens the Transfer Leads workflow pre-scoped to
 * that counselor via the Central Lead Distribution page. */
export const InactiveCounselorLeadsCard = () => {
  const navigate = useNavigate();
  const { selectedBranchId } = useBranchFilter();
  const query = useQuery({
    queryKey: ['dashboard', 'inactive-counselor-leads', selectedBranchId],
    queryFn: () => getCounselorRanking(undefined, selectedBranchId),
  });

  const inactive = (query.data ?? []).filter((c) => !c.isActive && c.leadsAssigned > 0);

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1">Leads of Inactive Counselors</Typography>
          {inactive.length > 0 && <Chip size="small" color="error" label={inactive.length} />}
        </Stack>
        <Typography
          variant="caption"
          color="primary"
          sx={{ cursor: 'pointer', fontWeight: 600 }}
          onClick={() => navigate('/lead-distribution')}
        >
          Transfer leads
        </Typography>
      </Stack>
      {inactive.length === 0 ? (
        <EmptyState loading={query.isLoading} message="No leads assigned to inactive counselors" />
      ) : (
        <List dense disablePadding>
          {inactive.map((c) => (
            <ListItemButton key={c.id} onClick={() => navigate('/lead-distribution')} sx={{ borderRadius: 2, px: 1 }}>
              <ListItemText primary={`${c.firstName} ${c.lastName}`} secondary={`${c.leadsAssigned} lead(s) — Inactive`} />
            </ListItemButton>
          ))}
        </List>
      )}
    </Paper>
  );
};
